import { useState } from 'react'
import { Autocomplete, Button, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useUpdateCustomerVesselMapping } from 'src/api/vessel'
import { invalidateCustomer } from 'src/api/customer'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { ShowDialogProps } from 'src/show-dialog'
import { IVesselClient } from 'src/generated-types/vessel'

interface VesselMappingDialogProps extends ShowDialogProps {
  customerId: EntityId
  currentVesselId?: number
  vesselClients: IVesselClient[]
}

const VesselMappingDialog: React.FC<VesselMappingDialogProps> = ({
  pop,
  customerId,
  currentVesselId,
  vesselClients,
}) => {
  const { t } = useTranslation('common')

  const currentClient = vesselClients.find(c => c.clientId === currentVesselId) ?? null
  const [selected, setSelected] = useState<IVesselClient | null>(currentClient)

  const { mutate, isLoading } = useUpdateCustomerVesselMapping(customerId)

  const handleSave = () => {
    mutate(
      { vesselClientId: selected?.clientId ?? null },
      {
        onSuccess: () => {
          toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
          invalidateCustomer.getAdminCustomers()
          pop(true)
        },
        onError: () => {
          toast.error(t('errorMessage', { ns: 'common-api-message' }))
        },
      },
    )
  }

  return (
    <GenericDialogLayout pop={pop} removedCloseButton={true}>
      <Stack spacing={3} pt={1}>
        <Autocomplete
          options={vesselClients}
          getOptionLabel={option => option.name}
          value={selected}
          onChange={(_, newValue) => setSelected(newValue)}
          isOptionEqualToValue={(opt, val) => opt.clientId === val.clientId}
          renderInput={params => <TextField {...params} label={t('vessel-client', { ns: 'admin-customer' })} />}
        />
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => pop()} disabled={isLoading}>
            {t('cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            {t('save')}
          </Button>
        </Stack>
      </Stack>
    </GenericDialogLayout>
  )
}

export default VesselMappingDialog
